import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES, findCountry } from "@/lib/countries";
import { Check, ChevronDown } from "lucide-react";

/**
 * Phone input with combined searchable country code prefix.
 * Props:
 *  - value: { country: ISO2, number: string }
 *  - onChange: (next) => void
 *  - dark, required, name, testid
 */
export function PhoneInput({
  value = { country: "IN", number: "" },
  onChange,
  dark = false,
  required = false,
  testid = "phone",
  placeholder = "Phone number",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const current = useMemo(() => findCountry(value.country), [value.country]);

  const baseInput = dark
    ? "bg-[#10141C] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#FFD600]"
    : "bg-white border-neutral-300 text-[#0A0C10] placeholder:text-neutral-400 focus:border-[#002FA7]";

  return (
    <div className={`flex items-stretch ${className}`} data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-cc-trigger`}
            className={`inline-flex items-center gap-2 px-3 text-sm border ${baseInput} border-r-0 hover:border-[#FFD600]`}
          >
            <span className="text-base leading-none">{current?.flag}</span>
            <span className="font-mono">{current?.dial}</span>
            <ChevronDown size={12} className="opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className={`p-0 w-72 ${dark ? "bg-[#10141C] border-white/10 text-white" : ""}`} align="start">
          <Command className={dark ? "bg-[#10141C] text-white" : ""}>
            <CommandInput placeholder="Search country or code…" data-testid={`${testid}-cc-search`} className={dark ? "text-white placeholder:text-neutral-500" : ""} />
            <CommandList className="max-h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.dial} ${c.code}`}
                    onSelect={() => {
                      onChange?.({ ...value, country: c.code });
                      setOpen(false);
                    }}
                    data-testid={`${testid}-cc-opt-${c.code}`}
                    className={dark ? "data-[selected=true]:bg-white/10 text-white" : ""}
                  >
                    <Check size={14} className={`mr-2 ${value.country === c.code ? "opacity-100" : "opacity-0"}`} />
                    <span className="mr-2">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className={`ml-2 font-mono text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{c.dial}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <input
        type="tel"
        data-testid={`${testid}-number`}
        required={required}
        placeholder={placeholder}
        value={value.number || ""}
        onChange={(e) => onChange?.({ ...value, number: e.target.value.replace(/[^0-9\-\s]/g, "") })}
        className={`flex-1 min-w-0 px-3 py-2.5 text-sm border ${baseInput} outline-none`}
      />
    </div>
  );
}

export function combinePhone(v) {
  if (!v) return "";
  const c = findCountry(v.country);
  return v.number ? `${c?.dial || ""} ${v.number}`.trim() : "";
}
